// iter155 Part C — Excel templates (export + parse) for setup and monthly
// daily-entry bulk capture. Reuses `exceljs` (already installed in Part B).
//
// Exports:
//   downloadSetupTemplate({ profile, vendors, cchs }) → Blob download
//   downloadDailyEntryTemplate({ year, month, entries }) → Blob download
//   parseSetupTemplate(file) → { profile, vendors, cchs, errors: [] }
//   parseDailyEntryTemplate(file) → { entries, errors: [] }
//
// Parsers always coerce + validate against the same shape the backend
// expects. Any row-level problem is surfaced in `errors` with 1-based row
// numbers so the UI can show them in a toast / modal.

import { NO_MDM_REASONS } from "./dayKind";

const CLASS_KEYS = ["balVatika", "primary", "upperPrimary", "highSchool"];
const DAY_KINDS = ["working", "working_no_mdm", "holiday"];
const REASON_KEYS = NO_MDM_REASONS.map((r) => r.key);

// -------- Shared helpers --------
async function _loadExcelJs() {
  const mod = await import("exceljs");
  return mod.default;
}

function _applyHeaderStyle(ws, rowNum, bgArgb = "FF2C5F2D") {
  const row = ws.getRow(rowNum);
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgArgb } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
  });
  row.height = 32;
}

function _addListValidation(ws, colLetter, firstRow, lastRow, values) {
  for (let r = firstRow; r <= lastRow; r += 1) {
    ws.getCell(`${colLetter}${r}`).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [`"${values.join(",")}"`],
    };
  }
}

function _save(wb, filename) {
  return wb.xlsx.writeBuffer().then((buf) => {
    const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  });
}

// =====================================================================
// Setup Template
// =====================================================================

const SCHOOL_COLS = [
  { key: "schoolName",     label_en: "School Name",         label_od: "ବିଦ୍ୟାଳୟ ନାମ",       width: 24 },
  { key: "schoolNameOdia", label_en: "School Name (Odia)",  label_od: "ବିଦ୍ୟାଳୟ ନାମ (ଓଡ଼ିଆ)", width: 24 },
  { key: "udiseCode",      label_en: "UDISE (11 digits)",   label_od: "UDISE",            width: 16 },
  { key: "district",       label_en: "District",            label_od: "ଜିଲ୍ଲା",            width: 14 },
  { key: "districtOdia",   label_en: "District (Odia)",     label_od: "ଜିଲ୍ଲା (ଓଡ଼ିଆ)",     width: 14 },
  { key: "block",          label_en: "Block",               label_od: "ବ୍ଲକ୍",             width: 14 },
  { key: "blockOdia",      label_en: "Block (Odia)",        label_od: "ବ୍ଲକ୍ (ଓଡ଼ିଆ)",      width: 14 },
  { key: "village",        label_en: "Village",             label_od: "ଗ୍ରାମ",             width: 14 },
  { key: "villageOdia",    label_en: "Village (Odia)",      label_od: "ଗ୍ରାମ (ଓଡ଼ିଆ)",      width: 14 },
  { key: "post",           label_en: "Post",                label_od: "Post",             width: 14 },
  { key: "postOdia",       label_en: "Post (Odia)",         label_od: "Post (ଓଡ଼ିଆ)",       width: 14 },
  { key: "thana",          label_en: "Thana",               label_od: "ଥାନା",              width: 14 },
  { key: "thanaOdia",      label_en: "Thana (Odia)",        label_od: "ଥାନା (ଓଡ଼ିଆ)",       width: 14 },
  { key: "pincode",        label_en: "Pincode",             label_od: "Pincode",          width: 10 },
  { key: "pincodeOdia",    label_en: "Pincode (Odia)",      label_od: "Pincode (ଓଡ଼ିଆ)",   width: 12 },
];

const ROW_COLS = [
  { key: "entityType",     label_en: "Entity Type",         label_od: "ପ୍ରକାର (individual / agency)", width: 16, dropdown: ["individual", "agency"] },
  { key: "name",           label_en: "Name",                label_od: "ନାମ",                 width: 18 },
  { key: "nameOdia",       label_en: "Name (Odia)",         label_od: "ନାମ (ଓଡ଼ିଆ)",          width: 18 },
  { key: "category",       label_en: "Category",            label_od: "Category",           width: 14 },
  { key: "relationType",   label_en: "Relation Type",       label_od: "father / husband",   width: 14, dropdown: ["father", "husband"] },
  { key: "fatherName",     label_en: "Father/Husband Name", label_od: "ପିତା/ସ୍ୱାମୀଙ୍କ ନାମ",   width: 18 },
  { key: "fatherNameOdia", label_en: "Father/Husband (Odia)", label_od: "ପିତା/ସ୍ୱାମୀଙ୍କ ନାମ (ଓଡ଼ିଆ)", width: 18 },
  { key: "village",        label_en: "Village",             label_od: "ଗ୍ରାମ",               width: 14 },
  { key: "villageOdia",    label_en: "Village (Odia)",      label_od: "ଗ୍ରାମ (ଓଡ଼ିଆ)",       width: 14 },
  { key: "post",           label_en: "Post",                label_od: "Post",               width: 14 },
  { key: "postOdia",       label_en: "Post (Odia)",         label_od: "Post (ଓଡ଼ିଆ)",        width: 14 },
  { key: "thana",          label_en: "Thana",               label_od: "ଥାନା",                width: 14 },
  { key: "thanaOdia",      label_en: "Thana (Odia)",        label_od: "ଥାନା (ଓଡ଼ିଆ)",        width: 14 },
  { key: "block",          label_en: "Block",               label_od: "ବ୍ଲକ୍",               width: 14 },
  { key: "blockOdia",      label_en: "Block (Odia)",        label_od: "ବ୍ଲକ୍ (ଓଡ଼ିଆ)",       width: 14 },
  { key: "district",       label_en: "District",            label_od: "ଜିଲ୍ଲା",              width: 14 },
  { key: "districtOdia",   label_en: "District (Odia)",     label_od: "ଜିଲ୍ଲା (ଓଡ଼ିଆ)",      width: 14 },
  { key: "pincode",        label_en: "Pincode",             label_od: "Pincode",            width: 10 },
  { key: "pincodeOdia",    label_en: "Pincode (Odia)",      label_od: "Pincode (ଓଡ଼ିଆ)",     width: 12 },
  { key: "phone",          label_en: "Phone (optional)",    label_od: "ଫୋନ୍",                width: 14 },
];

/** Build + download the setup template, pre-populated with existing data if provided. */
export async function downloadSetupTemplate({ profile, vendors = [], cchs = [] } = {}) {
  const ExcelJS = await _loadExcelJs();
  const wb = new ExcelJS.Workbook();
  wb.creator = "MDM Sahayak";
  wb.created = new Date();

  // Sheet 0 — Instructions
  const si = wb.addWorksheet("README");
  si.addRow(["MDM Sahayak — Setup Template"]).getCell(1).font = { bold: true, size: 16 };
  si.addRow([]);
  si.addRow(["1. Fill the School Profile sheet (one row)."]);
  si.addRow(["2. Add every rice/vegetable/dairy/etc vendor to the Vendors sheet."]);
  si.addRow(["3. Add every cook-cum-helper to the CCHs sheet."]);
  si.addRow(["4. Odia columns are optional for agency rows — fill only name + agencyName (Odia)."]);
  si.addRow(["5. Upload the file back on the Setup page to apply."]);
  si.getColumn(1).width = 100;

  // Sheet 1 — School Profile
  const sp = wb.addWorksheet("School Profile");
  sp.columns = SCHOOL_COLS.map((c) => ({ header: `${c.label_en} / ${c.label_od}`, key: c.key, width: c.width }));
  _applyHeaderStyle(sp, 1);
  if (profile) sp.addRow(SCHOOL_COLS.reduce((o, c) => ({ ...o, [c.key]: profile[c.key] || "" }), {}));
  else sp.addRow({}); // blank row

  // Sheet 2 — Vendors
  const sv = wb.addWorksheet("Vendors");
  sv.columns = ROW_COLS.map((c) => ({ header: `${c.label_en} / ${c.label_od}`, key: c.key, width: c.width }));
  _applyHeaderStyle(sv, 1);
  const vendorRows = vendors.length ? vendors : [{}];
  vendorRows.forEach((v) => sv.addRow(ROW_COLS.reduce((o, c) => ({ ...o, [c.key]: v[c.key] || "" }), {})));
  // Add dropdown validations
  ROW_COLS.forEach((c, idx) => {
    if (c.dropdown) {
      const colLetter = String.fromCharCode(65 + idx);
      _addListValidation(sv, colLetter, 2, Math.max(300, vendorRows.length + 50), c.dropdown);
    }
  });

  // Sheet 3 — CCHs (same shape as Vendors; adds a few CCH-only hints)
  const sc = wb.addWorksheet("CCHs");
  sc.columns = ROW_COLS.map((c) => ({ header: `${c.label_en} / ${c.label_od}`, key: c.key, width: c.width }));
  _applyHeaderStyle(sc, 1);
  const cchRows = cchs.length ? cchs : [{}];
  cchRows.forEach((c0) => sc.addRow(ROW_COLS.reduce((o, c) => ({ ...o, [c.key]: c0[c.key] || "" }), {})));
  ROW_COLS.forEach((c, idx) => {
    if (c.dropdown) {
      const colLetter = String.fromCharCode(65 + idx);
      _addListValidation(sc, colLetter, 2, Math.max(300, cchRows.length + 50), c.dropdown);
    }
  });

  const udise = (profile?.udiseCode || "school").replace(/\W+/g, "");
  await _save(wb, `mdm-sahayak-setup-${udise}.xlsx`);
}

/** Parse an uploaded setup workbook. Returns normalised shapes + per-row errors. */
export async function parseSetupTemplate(file) {
  const ExcelJS = await _loadExcelJs();
  const wb = new ExcelJS.Workbook();
  const buf = await file.arrayBuffer();
  await wb.xlsx.load(buf);

  const errors = [];
  // School Profile
  let profile = null;
  const sp = wb.getWorksheet("School Profile");
  if (sp) {
    const row = sp.getRow(2);
    const headerRow = sp.getRow(1);
    const map = {};
    headerRow.eachCell((cell, col) => {
      // Match either English-only or combined "En / Od" header
      const h = String(cell.value || "").split("/")[0].trim();
      const found = SCHOOL_COLS.find((c) => c.label_en.trim() === h);
      if (found) map[col] = found.key;
    });
    const obj = {};
    row.eachCell((cell, col) => {
      const key = map[col];
      if (key) obj[key] = cell.text == null ? "" : String(cell.text).trim();
    });
    if (Object.keys(obj).length) profile = obj;
    if (profile?.udiseCode && !/^\d{11}$/.test(profile.udiseCode)) {
      errors.push({ sheet: "School Profile", row: 2, msg: "udiseCode must be exactly 11 digits" });
    }
  } else {
    errors.push({ sheet: "School Profile", row: 0, msg: "missing 'School Profile' sheet" });
  }
  // Vendors + CCHs
  const extractRows = (sheetName) => {
    const ws = wb.getWorksheet(sheetName);
    if (!ws) {
      errors.push({ sheet: sheetName, row: 0, msg: `missing '${sheetName}' sheet` });
      return [];
    }
    const headerRow = ws.getRow(1);
    const map = {};
    headerRow.eachCell((cell, col) => {
      const h = String(cell.value || "").split("/")[0].trim();
      const found = ROW_COLS.find((c) => c.label_en.trim() === h);
      if (found) map[col] = found.key;
    });
    const rows = [];
    ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      if (rowNumber === 1) return;
      const obj = {};
      row.eachCell((cell, col) => {
        const key = map[col];
        if (key) obj[key] = cell.text == null ? "" : String(cell.text).trim();
      });
      if (!Object.values(obj).some((v) => v)) return; // skip empty row
      if (obj.relationType && !["father", "husband"].includes(obj.relationType)) {
        errors.push({ sheet: sheetName, row: rowNumber, msg: `relationType must be 'father' or 'husband' (got '${obj.relationType}')` });
      }
      if (obj.entityType && !["individual", "agency"].includes(obj.entityType)) {
        errors.push({ sheet: sheetName, row: rowNumber, msg: `entityType must be 'individual' or 'agency' (got '${obj.entityType}')` });
      }
      if (!obj.name) {
        errors.push({ sheet: sheetName, row: rowNumber, msg: "name is required" });
      }
      obj.entityType = obj.entityType || "individual";
      obj.relationType = obj.relationType || "father";
      rows.push(obj);
    });
    return rows;
  };
  const vendors = extractRows("Vendors");
  const cchs = extractRows("CCHs");

  return { profile, vendors, cchs, errors };
}

// =====================================================================
// Monthly Daily Entry Template
// =====================================================================

const DAILY_COLS = [
  { key: "date",           label_en: "Date (YYYY-MM-DD)",        width: 14 },
  { key: "dayOfWeek",      label_en: "Day",                      width: 10 },
  { key: "dayKind",        label_en: "Day Kind",                 width: 18, dropdown: DAY_KINDS },
  { key: "noMdmReason",    label_en: "No-MDM Reason",            width: 20, dropdown: REASON_KEYS },
  { key: "noMdmReasonOther", label_en: "Reason (if other)",      width: 20 },
  { key: "orderNo",        label_en: "Order No (collector_order)", width: 16 },
  { key: "orderDate",      label_en: "Order Date (collector_order)", width: 14 },
  { key: "balVatikaAttendance", label_en: "Bal Vatika Present",  width: 16 },
  { key: "primaryAttendance",   label_en: "Primary Present",      width: 16 },
  { key: "upperPrimaryAttendance", label_en: "Upper Primary Present", width: 18 },
  { key: "highSchoolAttendance", label_en: "High School Present", width: 18 },
  { key: "notes",          label_en: "Notes",                    width: 24 },
];

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export async function downloadDailyEntryTemplate({ year, month, entries = [] } = {}) {
  const ExcelJS = await _loadExcelJs();
  const wb = new ExcelJS.Workbook();
  wb.creator = "MDM Sahayak";
  wb.created = new Date();

  // Reference sheet
  const sr = wb.addWorksheet("Reference");
  sr.addRow(["Reference — valid Day Kind values"]).getCell(1).font = { bold: true, size: 13 };
  DAY_KINDS.forEach((k) => sr.addRow([k]));
  sr.addRow([]);
  sr.addRow(["Reference — valid No-MDM Reason keys (en / od)"]).getCell(1).font = { bold: true, size: 13 };
  NO_MDM_REASONS.forEach((r) => sr.addRow([r.key, r.en, r.od]));
  sr.getColumn(1).width = 20;
  sr.getColumn(2).width = 32;
  sr.getColumn(3).width = 32;

  // Daily sheet
  const sd = wb.addWorksheet("Daily Entries");
  sd.addRow([`${year}-${String(month).padStart(2, "0")} Daily Entries`]).getCell(1).font = { bold: true, size: 14 };
  sd.addRow([]);
  sd.addRow(DAILY_COLS.map((c) => c.label_en));
  _applyHeaderStyle(sd, 3);
  DAILY_COLS.forEach((c, i) => { sd.getColumn(i + 1).width = c.width; });

  const existing = new Map();
  (entries || []).forEach((e) => existing.set(e.date, e));
  const last = new Date(year, month, 0).getDate();
  for (let d = 1; d <= last; d += 1) {
    const iso = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const dow = DAY_NAMES[new Date(iso + "T00:00:00").getDay()];
    const e = existing.get(iso) || {};
    const att = e.attendance || {};
    sd.addRow([
      iso,
      dow,
      e.dayKind || "working",
      e.noMdmReason || "",
      e.noMdmReasonOther || "",
      e.orderNo || "",
      e.orderDate || "",
      Number(att.balVatika || 0),
      Number(att.primary || 0),
      Number(att.upperPrimary || 0),
      Number(att.highSchool || 0),
      e.notes || "",
    ]);
  }
  // Dropdown validations
  const firstRow = 4;
  const lastRow = firstRow + last - 1;
  _addListValidation(sd, "C", firstRow, lastRow, DAY_KINDS);
  _addListValidation(sd, "D", firstRow, lastRow, REASON_KEYS);

  await _save(wb, `mdm-sahayak-daily-${year}-${String(month).padStart(2, "0")}.xlsx`);
}

/** Parse an uploaded monthly daily-entry template. */
export async function parseDailyEntryTemplate(file) {
  const ExcelJS = await _loadExcelJs();
  const wb = new ExcelJS.Workbook();
  const buf = await file.arrayBuffer();
  await wb.xlsx.load(buf);
  const sd = wb.getWorksheet("Daily Entries");
  if (!sd) return { entries: [], errors: [{ sheet: "Daily Entries", row: 0, msg: "missing 'Daily Entries' sheet" }] };
  const errors = [];
  const entries = [];
  const headerRow = sd.getRow(3);
  const map = {};
  headerRow.eachCell((cell, col) => {
    const h = String(cell.value || "").trim();
    const found = DAILY_COLS.find((c) => c.label_en === h);
    if (found) map[col] = found.key;
  });
  sd.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber <= 3) return;
    const obj = {};
    row.eachCell((cell, col) => {
      const key = map[col];
      if (key) obj[key] = cell.text == null ? "" : String(cell.text).trim();
    });
    if (!obj.date) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(obj.date)) {
      errors.push({ sheet: "Daily Entries", row: rowNumber, msg: `invalid date '${obj.date}'` });
      return;
    }
    const dayKind = obj.dayKind || "working";
    if (!DAY_KINDS.includes(dayKind)) {
      errors.push({ sheet: "Daily Entries", row: rowNumber, msg: `invalid dayKind '${dayKind}'` });
      return;
    }
    if (dayKind === "working_no_mdm" && !obj.noMdmReason) {
      errors.push({ sheet: "Daily Entries", row: rowNumber, msg: "noMdmReason required for working_no_mdm" });
      return;
    }
    if (obj.noMdmReason && !REASON_KEYS.includes(obj.noMdmReason)) {
      errors.push({ sheet: "Daily Entries", row: rowNumber, msg: `invalid noMdmReason '${obj.noMdmReason}'` });
      return;
    }
    const attendance = {
      balVatika: Number(obj.balVatikaAttendance || 0),
      primary: Number(obj.primaryAttendance || 0),
      upperPrimary: Number(obj.upperPrimaryAttendance || 0),
      highSchool: Number(obj.highSchoolAttendance || 0),
    };
    if (dayKind !== "working" && CLASS_KEYS.some((k) => attendance[k] > 0)) {
      errors.push({ sheet: "Daily Entries", row: rowNumber, msg: `attendance must be 0 for ${dayKind}` });
      return;
    }
    entries.push({
      date: obj.date,
      entryType: "mdm",
      dayKind,
      noMdmReason: dayKind === "working_no_mdm" ? obj.noMdmReason : undefined,
      noMdmReasonOther: obj.noMdmReasonOther || undefined,
      orderNo: obj.orderNo || undefined,
      orderDate: obj.orderDate || undefined,
      attendance,
      notes: obj.notes || "",
      ingredients: [],
    });
  });
  return { entries, errors };
}
